import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';

import type {
  AverageStat,
  BaseballAverageStatType,
  BasketballAverageStatType,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconMinus } from '@sorare/core/src/atoms/icons/system/iconMinus.ts';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';
import {
  PlayerSearchViewAttributes,
  StatsDisplay,
} from '@sorare/core/src/lib/playerSearchView';

import { Cell } from '#src/components/searchPlayerTable/SearchTable/ui.tsx';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import type { StatCell_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  stat: AverageStat | BasketballAverageStatType | BaseballAverageStatType;
  anyPlayer: StatCell_anyPlayer;
  scopedFootballAverageStats?: readonly {
    stat: AverageStat;
    value: number | null;
  }[];
  scopedFootballTotalStats?: readonly {
    stat: AverageStat;
    value: number | null;
  }[];
};

export const StatCell = ({
  stat,
  anyPlayer,
  scopedFootballAverageStats,
  scopedFootballTotalStats,
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();
  const displayMode = statsManager[PlayerSearchViewAttributes.STATS_DISPLAY];

  const { allAverageStats, allTotalStats } = (() => {
    if (isType(anyPlayer, 'BaseballPlayer'))
      return {
        allAverageStats: anyPlayer.baseballAllAverageStats,
        allTotalStats: anyPlayer.baseballAllTotalStats,
      };

    if (isType(anyPlayer, 'NBAPlayer'))
      return {
        allAverageStats: anyPlayer.nbaAllAverageStats,
        allTotalStats: anyPlayer.nbaAllTotalStats,
      };
    if (isType(anyPlayer, 'Player'))
      return {
        allAverageStats:
          scopedFootballAverageStats ?? anyPlayer.footballAllAverageStats,
        allTotalStats:
          scopedFootballTotalStats ?? anyPlayer.footballAllTotalStats,
      };
    return {};
  })();

  if (!allAverageStats || !allTotalStats) return null;

  const statToDisplay = (
    displayMode === StatsDisplay.TOTAL ? allTotalStats : allAverageStats
  )?.find(s => s.stat === stat)?.value;

  return (
    <Cell>
      <BodyS bold>
        {statToDisplay === null || statToDisplay === undefined ? (
          <FontAwesomeIcon icon={iconMinus} color="var(--c-nd-500)" />
        ) : (
          <FormattedNumber
            value={Number(statToDisplay)}
            style="decimal"
            minimumFractionDigits={0}
            maximumFractionDigits={2}
          />
        )}
      </BodyS>
    </Cell>
  );
};

StatCell.fragments = {
  anyPlayer: gql`
    fragment StatCell_anyPlayer on AnyPlayerInterface {
      slug
      ... on BaseballPlayer {
        slug
        baseballAllAverageStats: allAverageStats {
          stat
          value
        }
        baseballAllTotalStats: allTotalStats {
          stat
          value
        }
      }
      ... on NBAPlayer {
        slug
        nbaAllAverageStats: allAverageStats {
          stat
          value
        }
        nbaAllTotalStats: allTotalStats {
          stat
          value
        }
      }
      ... on Player {
        slug
        footballAllAverageStats: allAverageStats(limit: $averageLimit) {
          stat
          value
        }
        footballAllTotalStats: allTotalStats {
          stat
          value
        }
      }
    }
  ` as TypedDocumentNode<StatCell_anyPlayer>,
};
